import { ENDPOINTS as STREAM_ENDPOINTS } from '@polyai/stream/endpoints';

import ENDPOINTS from 'api/endpoints';
import { doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type { EndResponse, RespondBody, RespondResponse } from './types';

export const startChat = ({
  accountId,
  projectId,
  clientEnv,
  languageCode,
  variantId,
  channel,
}: {
  accountId: string;
  projectId: string;
  clientEnv: string;
  languageCode: string;
  variantId?: string;
  channel?: string;
}) => {
  return doPost<RespondResponse>(
    generateNextPath(ENDPOINTS.START_CHAT, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      client_env: clientEnv,
      asr_lang_code: languageCode,
      tts_lang_code: languageCode,
      variant_id: variantId,
      channel,
    },
    { version: 'v2' },
  );
};

export const respond = ({
  accountId,
  projectId,
  conversationId,
  clientEnv,
  body,
}: {
  accountId: string;
  projectId: string;
  conversationId: string;
  clientEnv: string;
  body: RespondBody;
}) => {
  return doPost<RespondResponse>(
    generateNextPath(ENDPOINTS.RESPOND, {
      accountId,
      projectId,
      conversationId,
    }),
    LOGGER_TYPE.JUPITER,
    { ...body, client_env: clientEnv },
    { version: 'v2' },
  );
};

export const startDraftChat = ({
  accountId,
  projectId,
  languageCode,
  variantId,
  channel,
  artifactVersion,
  lambdaDeploymentVersion,
}: {
  accountId: string;
  projectId: string;
  languageCode: string;
  variantId?: string;
  channel?: string;
  artifactVersion?: string;
  lambdaDeploymentVersion?: string;
}) => {
  return doPost<RespondResponse>(
    generateNextPath(ENDPOINTS.DRAFT_START_CHAT, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      asr_lang_code: languageCode,
      tts_lang_code: languageCode,
      variant_id: variantId,
      channel,
      artifact_version: artifactVersion,
      lambda_deployment_version: lambdaDeploymentVersion,
    },
    { version: 'v2' },
  );
};

export const draftRespond = ({
  accountId,
  projectId,
  conversationId,
  body,
}: {
  accountId: string;
  projectId: string;
  conversationId: string;
  body: RespondBody;
}) => {
  return doPost<RespondResponse>(
    generateNextPath(ENDPOINTS.DRAFT_RESPOND, {
      accountId,
      projectId,
      conversationId,
    }),
    LOGGER_TYPE.JUPITER,
    body,
    { version: 'v2' },
  );
};

export const startTemplateChat = ({
  projectId,
  languageCode,
}: {
  projectId: string;
  languageCode: string;
}) => {
  return doPost<RespondResponse>(
    generateNextPath(ENDPOINTS.TEMPLATE_START_CHAT, { projectId }),
    LOGGER_TYPE.JUPITER,
    {
      asr_lang_code: languageCode,
      tts_lang_code: languageCode,
    },
    { version: 'v2' },
  );
};

export const templateRespond = ({
  projectId,
  conversationId,
  body,
}: {
  accountId: string;
  projectId: string;
  conversationId: string;
  clientEnv: string;
  body: RespondBody;
}) => {
  return doPost<RespondResponse>(
    generateNextPath(ENDPOINTS.TEMPLATE_RESPOND, { projectId, conversationId }),
    LOGGER_TYPE.JUPITER,
    body,
    { version: 'v2' },
  );
};

export const endChat = ({
  accountId,
  projectId,
  conversationId,
  clientEnv,
  languageCode,
  channelType,
}: {
  accountId: string;
  projectId: string;
  conversationId: string;
  clientEnv: string;
  languageCode: string;
  channelType?: string;
}) => {
  return doPost<EndResponse>(
    generateNextPath(ENDPOINTS.END_CHAT, {
      accountId,
      projectId,
      conversationId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      client_env: clientEnv === 'draft' ? 'sandbox' : clientEnv,
      asr_language_code: languageCode,
      tts_language_code: languageCode,
      ...(channelType && { channel_type: channelType }),
    },
    { version: 'v2' },
  );
};

export const endTemplateChat = ({
  projectId,
  conversationId,
}: {
  accountId: string;
  projectId: string;
  conversationId: string;
  clientEnv: string;
}) => {
  return doPost<EndResponse>(
    generateNextPath(ENDPOINTS.TEMPLATE_END_CHAT, {
      projectId,
      conversationId,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export type PrepareDraftChatResponse = {
  artifactVersion: string;
  lambdaDeploymentVersion: string;
};

export type PrepareDraftCallResponse = {
  artifactVersion: string;
  ragKBVersion: string;
  lambdaDeploymentVersion: string;
  authToken: string;
};

export const prepareDraftChat = ({
  accountId,
  projectId,
  branchId,
  expectedBranchLastKnownSequence,
}: {
  accountId: string;
  projectId: string;
  branchId: string;
  expectedBranchLastKnownSequence: number;
}) => {
  return doPost<PrepareDraftChatResponse>(
    generateNextPath(STREAM_ENDPOINTS.BRANCH_CHAT, {
      accountId,
      projectId,
      branchId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      expectedBranchLastKnownSequence: Number(expectedBranchLastKnownSequence),
    },
    {
      prefix: 'sourcerer',
    },
  );
};

export const prepareDraftCall = ({
  accountId,
  projectId,
  branchId,
  expectedBranchLastKnownSequence,
}: {
  accountId: string;
  projectId: string;
  branchId: string;
  expectedBranchLastKnownSequence: number;
}) => {
  return doPost<PrepareDraftCallResponse>(
    generateNextPath(STREAM_ENDPOINTS.BRANCH_DEPLOY, {
      accountId,
      projectId,
      branchId,
    }),
    LOGGER_TYPE.JUPITER,
    {
      expectedBranchLastKnownSequence: Number(expectedBranchLastKnownSequence),
    },
    {
      prefix: 'sourcerer',
    },
  );
};
